import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {IDENTITY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import EmptySidebarIcon from 'sidebar/EmptySidebarIcon';
import SidebarItem from 'sidebar/SidebarItem';
import * as Colors from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as nyc from 'ui-library/NYC';
import {assertNever} from 'util/Types';

import {CurrentTenantQueryApp} from './__generated/CurrentTenantQueryApp';
import BenchlingAppProjectPolicyTable from './BenchlingAppProjectPolicyTable';
import BenchlingAppRegistryPolicyTable from './BenchlingAppRegistryPolicyTable';

export const CURRENT_TENANT_QUERY_APP = gql`
  query CurrentTenantQueryApp @skipAudit @engComponent(name: BENCHLING_APPS) {
    currentTenant {
      id
      displayName
    }
  }
`;

enum ResourceType {
  PROJECT = 'PROJECT',
  REGISTRY = 'REGISTRY',
}

const RESOURCE_TYPE_TO_DISPLAY: {[key in ResourceType]: string} = {
  [ResourceType.PROJECT]: 'Projects',
  [ResourceType.REGISTRY]: 'Registries',
};

interface SidepanelProps {
  activeResourceType: ResourceType;
  onChangeActiveResourceType: (resourceType: ResourceType) => void;
}

function Sidepanel(props: SidepanelProps): JSX.Element {
  return (
    <>
      <nyc.Row className={css(styles.sidebarTitle)}>
        <EmptySidebarIcon />
        <strong>Resource Types</strong>
      </nyc.Row>
      {Object.keys(ResourceType).map((resourceType: ResourceType) => (
        <SidebarItem
          key={resourceType}
          active={props.activeResourceType === resourceType}
          name={RESOURCE_TYPE_TO_DISPLAY[resourceType]}
          icon={<EmptySidebarIcon />}
          clickHandler={() => {
            props.onChangeActiveResourceType(resourceType);
          }}
        />
      ))}
    </>
  );
}

export interface BenchlingAppDataAccessTabProps {
  appId: string;
}

function BenchlingAppDataAccessTabSection(props: {
  resourceType: string;
  children: JSX.Element;
}): JSX.Element | null {
  const result = useBenchlingQuery<CurrentTenantQueryApp>(CURRENT_TENANT_QUERY_APP);
  if (result.type === QueryResultType.PENDING) {
    return (
      <div className={css(styles.benchlingAppDataAccessTabSection)}>
        <CenteredHorizontalThrobber />
      </div>
    );
  }
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load tenant details; try reloading the page.');
    return null;
  }
  return (
    <div className={css(styles.benchlingAppDataAccessTabSection)}>
      <p className={css(styles.description)}>
        The app has access to the following {pluralize(2, props.resourceType.toLowerCase())} in{' '}
        {result.data.currentTenant.displayName} that you have access to. You can see which access policies the
        app has been granted per {props.resourceType.toLowerCase()} and view more details on their effective
        permissions.
      </p>
      <p className={css(styles.description)}>
        This view is limited to {pluralize(2, props.resourceType.toLowerCase())} you can also access. As a
        result you may not see a complete list of all the {pluralize(2, props.resourceType.toLowerCase())} for
        which this app is a collaborator.
      </p>
      {props.children}
    </div>
  );
}

function PolicyTable(props: {resourceType: ResourceType; appId: string}): JSX.Element {
  switch (props.resourceType) {
    case ResourceType.PROJECT:
      return <BenchlingAppProjectPolicyTable appId={props.appId} />;
    case ResourceType.REGISTRY:
      return <BenchlingAppRegistryPolicyTable appId={props.appId} />;
    default:
      assert(false, `Unexpected resource type ${assertNever(props.resourceType)}`);
      return <></>;
  }
}

export default function BenchlingAppDataAccessTab(props: BenchlingAppDataAccessTabProps): JSX.Element {
  useOnMount(() => {
    analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.OPENED_PARTY_DATA_ACCESS_TAB, {
      objectType: 'app',
      objectId: props.appId,
    });
  });
  const [activeResourceType, setActiveResourceType] = useState(ResourceType.PROJECT);
  return (
    <nyc.Container size="lg" className={css(styles.container)}>
      <nyc.Row className={css(styles.containerRow)}>
        <nyc.Col lg={4} className={classNames('sidepanel', css(styles.sidepanelColumn))}>
          <Sidepanel
            activeResourceType={activeResourceType}
            onChangeActiveResourceType={setActiveResourceType}
          />
        </nyc.Col>
        <nyc.Col lg={18}>
          <BenchlingAppDataAccessTabSection resourceType={activeResourceType}>
            <PolicyTable resourceType={activeResourceType} appId={props.appId} />
          </BenchlingAppDataAccessTabSection>
        </nyc.Col>
      </nyc.Row>
    </nyc.Container>
  );
}

const styles = StyleSheet.create({
  description: {fontSize: '13px', maxWidth: '1000px'},
  sidepanelColumn: {
    zIndex: 0,
    position: 'sticky',
    maxWidth: '182px',
    borderRight: `1px solid ${Colors.GRAY_LIGHT}`,
    boxShadow: `0px 0px`,
    overflowX: 'hidden',
  },
  container: {
    height: '100%',
    paddingLeft: 0,
  },
  containerRow: {
    height: '100%',
  },
  sidebarTitle: {
    padding: '20px 15px 5px 15px',
  },
  benchlingAppDataAccessTabSection: {
    padding: '35px 29px',
  },
});
